import { useMutation } from '@apollo/client';
import { useTranslation } from '@crehana/i18n';

import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';
import useReportsAmplitude from '@/views/AdminDashboard/hooks/useReportsAmplitude';

import SEND_TRACKS_REPORT from './graphql/ReportDownloadTracks.v4.b2b.graphql';
import type {
  ReportDownloadTracks,
  ReportDownloadTracksVariables,
} from './graphql/types/ReportDownloadTracks';

enum STATUS {
  SUCCESS = 'success',
  FAILED = 'error',
}

const useSendTracksReport = (slug: string) => {
  const { t } = useTranslation();
  const { openSnackbar } = useAdminDashboard();
  const { logSubmit } = useReportsAmplitude(slug);

  const [sendTracksReport, { data, loading, error }] = useMutation<
    ReportDownloadTracks,
    ReportDownloadTracksVariables
  >(SEND_TRACKS_REPORT, {
    context: { clientName: 'v4.b2b' },
    onCompleted({ trackReportOrganizationProgressCall }) {
      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (!trackReportOrganizationProgressCall?.success)
        openSnackbar({
          type: 'alert',
          msg: t('SNACKBAR_DEFAULT_ERROR'),
        });
    },
    onError() {
      openSnackbar({
        type: 'alert',
        msg: t('SNACKBAR_DEFAULT_ERROR'),
      });
    },
  });

  const submitReport = async (variables: ReportDownloadTracksVariables) => {
    const { data } = await sendTracksReport({
      variables,
    });
    logSubmit({
      event: 'Send Report',
      reportType: 'Tracks Report',
      filters: variables,
      status: data?.trackReportOrganizationProgressCall?.success
        ? STATUS.SUCCESS
        : STATUS.FAILED,
    });
    return data;
  };

  return {
    submitReport,
    loading,
    error,
    data,
  };
};

export default useSendTracksReport;
